'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AnimationItem } from 'lottie-web';

import {
    getLoaderAnimationKind,
    isVideoContentType,
    type LoaderAnimationKind,
} from '@/lib/utils/storeSettings';
import {
    STORE_LOADING_COVER_ANIMATION_SIZE,
    STORE_LOADING_COVER_TEST_IDS,
} from '../StoreLoadingCover.consts';
import { StoreLoadingCoverLabel } from './StoreLoadingCoverLabel';
import { StoreLoadingCoverSpinner } from './StoreLoadingCoverSpinner';

interface IStoreLoadingCoverAnimationProps {
    url: string;
    /** Server-resolved kind; the client sniffs the Content-Type when unknown. */
    kind?: LoaderAnimationKind;
    /**
     * The cover is up at first paint: load the asset with the document.
     * Otherwise defer it to the window load event so it never competes with
     * the page's own critical resources.
     */
    eager: boolean;
    /** Shown with the default spinner only, i.e. when the asset cannot load. */
    fallbackLabel: string;
}

const SLOT_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: STORE_LOADING_COVER_ANIMATION_SIZE,
    height: STORE_LOADING_COVER_ANIMATION_SIZE,
    flexShrink: 0,
};

/**
 * The legacy automation id sits on the slot, not the spinner, because the
 * Playwright suites wait for that id to disappear.
 */
const SLOT_PROPS = {
    'data-testid': STORE_LOADING_COVER_TEST_IDS.legacySpinner,
    'aria-hidden': true,
    style: SLOT_STYLE,
} as const;

/**
 * A publisher's own processing animation in place of the built-in spinner.
 *
 * The slot is server-rendered at its final size so the default loader never
 * paints first: a video is a plain `<video>` the browser starts on its own
 * before any JavaScript, and a Lottie keeps the box empty until its JSON and
 * player are in hand. When the server could not classify the URL (media
 * library URLs carry no extension), the asset is fetched and the Content-Type
 * decides. Only when the asset itself cannot be loaded does the spinner take
 * over — that is the one case where "no loader" would be worse than the
 * default one.
 *
 * The cover is hidden with `display:none`, never unmounted, so both players
 * pause while it is off-screen: a looping video would otherwise keep decoding
 * and a Lottie would keep its frame loop running for the whole session.
 */
export function StoreLoadingCoverAnimation({
    url,
    kind: initialKind,
    eager,
    fallbackLabel,
}: IStoreLoadingCoverAnimationProps): React.ReactElement {
    const [kind, setKind] = useState<LoaderAnimationKind>(
        () => initialKind ?? getLoaderAnimationKind(url),
    );
    const [hasFailed, setHasFailed] = useState(false);
    const fail = useCallback(() => setHasFailed(true), []);
    const useVideo = useCallback(() => setKind('video'), []);

    if (hasFailed) {
        return (
            <React.Fragment>
                <StoreLoadingCoverSpinner />
                <StoreLoadingCoverLabel fallbackLabel={fallbackLabel} />
            </React.Fragment>
        );
    }

    if (kind === 'video') {
        return <VideoSlot url={url} eager={eager} onError={fail} />;
    }

    return <LottieSlot url={url} eager={eager} onVideo={useVideo} onError={fail} />;
}

interface IVideoSlotProps {
    url: string;
    eager: boolean;
    onError: () => void;
}

/**
 * Eager: `src` + `autoplay` are in the server HTML so the browser starts the
 * video before hydration. Deferred: the element ships without a source and
 * gets it once the page has loaded (or the cover shows), then is started by
 * hand — `autoplay` is left off so it cannot begin playing while hidden.
 */
function VideoSlot({ url, eager, onError }: IVideoSlotProps): React.ReactElement {
    const { element, isRendered, setElement } = useIsRendered<HTMLVideoElement>(eager);
    const shouldLoad = useDeferredStart(eager, isRendered);

    useEffect(() => {
        if (!element || !shouldLoad) return;
        if (isRendered) {
            if (element.paused) void element.play()?.catch(() => undefined);
        } else {
            element.pause();
        }
    }, [element, shouldLoad, isRendered]);

    return (
        <video
            {...SLOT_PROPS}
            ref={setElement}
            src={shouldLoad ? url : undefined}
            autoPlay={eager}
            loop
            muted
            playsInline
            preload={shouldLoad ? 'auto' : 'none'}
            width={STORE_LOADING_COVER_ANIMATION_SIZE}
            height={STORE_LOADING_COVER_ANIMATION_SIZE}
            style={{ ...SLOT_STYLE, objectFit: 'contain' }}
            onError={onError}
        />
    );
}

interface ILottieSlotProps {
    url: string;
    eager: boolean;
    /** The fetched asset turned out to be a video; the caller should play it. */
    onVideo: () => void;
    onError: () => void;
}

/**
 * lottie-web's light build (SVG renderer, no expressions) is 40% smaller than
 * the full player and enough for loader animations. Imported lazily so stores
 * without a custom animation never download it; started together with the
 * JSON fetch so the chunk downloads in parallel with it rather than after it.
 */
const loadLottie = () =>
    import('lottie-web/build/player/lottie_light').then((module) => module.default);

/**
 * Fetches the JSON and mounts the player only once both are ready, so the
 * slot never shows a half-loaded animation.
 */
function LottieSlot({ url, eager, onVideo, onError }: ILottieSlotProps): React.ReactElement {
    const { isRendered, setElement } = useIsRendered<HTMLDivElement>(eager);
    const shouldLoad = useDeferredStart(eager, isRendered);
    const isRenderedRef = useRef(isRendered);
    isRenderedRef.current = isRendered;
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<AnimationItem | null>(null);
    const [animationData, setAnimationData] = useState<unknown>(null);
    const lottieRef = useRef<Promise<typeof import('lottie-web').default> | null>(null);
    if (lottieRef.current === null && shouldLoad && typeof window !== 'undefined') {
        lottieRef.current = loadLottie();
    }

    useEffect(() => {
        if (!shouldLoad) return undefined;
        let isCurrent = true;
        fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load loader animation: ${response.status}`);
                }
                if (isVideoContentType(response.headers.get('content-type'))) {
                    return null;
                }
                return response.json();
            })
            .then((data) => {
                if (!isCurrent) return;
                if (data === null) onVideo();
                else setAnimationData(data);
            })
            .catch(() => {
                if (isCurrent) onError();
            });
        return () => {
            isCurrent = false;
        };
    }, [url, shouldLoad, onVideo, onError]);

    useEffect(() => {
        const container = containerRef.current;
        const lottieLoad = lottieRef.current;
        if (!animationData || !container || !lottieLoad) return undefined;
        let isCurrent = true;
        lottieLoad
            .then((lottie) => {
                if (!isCurrent) return;
                animationRef.current = lottie.loadAnimation({
                    container,
                    renderer: 'svg',
                    loop: true,
                    // The cover may already be hidden by the time the player mounts.
                    autoplay: isRenderedRef.current,
                    animationData,
                });
            })
            .catch(() => {
                if (isCurrent) onError();
            });
        return () => {
            isCurrent = false;
            animationRef.current?.destroy();
            animationRef.current = null;
        };
    }, [animationData, onError]);

    useEffect(() => {
        const animation = animationRef.current;
        if (!animation) return;
        if (isRendered) animation.play();
        else animation.pause();
    }, [isRendered, animationData]);

    return (
        <div {...SLOT_PROPS} ref={setElement}>
            {animationData ? (
                <div
                    ref={containerRef}
                    data-testid="store-loading-cover-lottie"
                    style={{
                        width: STORE_LOADING_COVER_ANIMATION_SIZE,
                        height: STORE_LOADING_COVER_ANIMATION_SIZE,
                    }}
                />
            ) : null}
        </div>
    );
}

interface IIsRendered<TElement extends Element> {
    element: TElement | null;
    /** `false` while the element sits in a `display:none` subtree. */
    isRendered: boolean;
    setElement: (element: TElement | null) => void;
}

/**
 * Tracks whether an element is currently laid out. An `IntersectionObserver`
 * reports a non-rendered (`display:none`) target as not intersecting and fires
 * on every toggle, which is exactly how the cover is shown and hidden. Until
 * the observer reports, the server's knowledge of the first paint stands in.
 */
function useIsRendered<TElement extends Element>(
    initialIsRendered: boolean,
): IIsRendered<TElement> {
    const [element, setElement] = useState<TElement | null>(null);
    const [isRendered, setIsRendered] = useState(initialIsRendered);

    useEffect(() => {
        if (!element || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            if (entry) setIsRendered(entry.isIntersecting);
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [element]);

    return { element, isRendered, setElement };
}

/**
 * Whether the asset may start loading. Eager slots start at once; deferred
 * ones wait for the window load event, or for the cover to show, whichever
 * comes first. Never goes back to `false`.
 */
function useDeferredStart(eager: boolean, isRendered: boolean): boolean {
    const [hasStarted, setHasStarted] = useState(eager);

    useEffect(() => {
        if (hasStarted) return undefined;
        if (isRendered || document.readyState === 'complete') {
            setHasStarted(true);
            return undefined;
        }
        const start = () => setHasStarted(true);
        window.addEventListener('load', start, { once: true });
        return () => window.removeEventListener('load', start);
    }, [hasStarted, isRendered]);

    return hasStarted;
}
